import { useEffect, useMemo } from "react"
import { Form, Input, InputNumber, Modal, Switch, TreeSelect } from "antd"
import { DICT_CODE_PATTERN, DICT_CODE_RULES } from "../constants"
import { SystemDict, SystemDictFormData, SystemDictModalProps } from "../types"
import { ID } from "@/types/common"

type TreeOption = {
  title: string
  value: SystemDict["id"]
  key: SystemDict["id"]
  children?: TreeOption[]
}

function collectDescendantIds(node?: SystemDict): Set<ID> {
  const ids = new Set<ID>()
  if (!node?.children?.length) return ids
  const stack = [...node.children]
  while (stack.length > 0) {
    const current = stack.pop()
    if (!current) continue
    ids.add(current.id)
    if (current.children?.length) {
      stack.push(...current.children)
    }
  }
  return ids
}

function buildParentOptions(items: SystemDict[], editingItem: SystemDict | null): TreeOption[] {
  const editingId = editingItem?.id
  const invalidIds = collectDescendantIds(editingItem ?? undefined)
  if (editingId) invalidIds.add(editingId)

  return items
    .filter((item) => item.nodeType === "CATEGORY")
    .map((item) => ({
      title: `${item.name} (${item.code})`,
      value: item.id,
      key: item.id,
      children: (item.children ?? [])
        .filter((child) => child.nodeType === "ITEM" && child.parentId === item.id)
        .filter((child) => !invalidIds.has(child.id))
        .map((child) => ({
          title: `${child.name} (${child.code})`,
          value: child.id,
          key: child.id,
        })),
    }))
}

const DictModal = ({
  isOpen,
  loading,
  item,
  parentForCreate,
  createMode = "CATEGORY",
  allItems,
  onClose,
  onSave,
}: SystemDictModalProps) => {
  const [form] = Form.useForm<SystemDictFormData>()
  const isEditMode = Boolean(item)
  const editingCategory = item?.nodeType === "CATEGORY"
  const creatingCategory = !item && createMode === "CATEGORY"
  const creatingItem = !item && createMode === "ITEM"

  const parentOptions = useMemo(() => {
    return buildParentOptions(allItems, item)
  }, [allItems, item])

  useEffect(() => {
    if (!isOpen) return

    if (item) {
      form.setFieldsValue({
        parentId: item.parentId,
        name: item.name,
        code: item.code,
        sort: item.sort,
        isEnabled: item.isEnabled,
        note: item.note ?? undefined,
      })
      return
    }

    form.setFieldsValue({
      parentId: parentForCreate?.id ?? null,
      name: "",
      code: "",
      sort: 0,
      isEnabled: true,
      note: "",
    })
  }, [form, isOpen, item, parentForCreate])

  const handleSubmit = async () => {
    const values = await form.validateFields()
    onSave({
      parentId: values.parentId ?? null,
      name: values.name.trim(),
      code: values.code.trim().toUpperCase(),
      sort: values.sort ?? 0,
      isEnabled: values.isEnabled ?? true,
      note: values.note?.trim(),
    })
  }

  return (
    <Modal
      title={
        isEditMode
          ? editingCategory
            ? "编辑字典类型"
            : "编辑字典项"
          : creatingCategory
            ? "新增字典类型"
            : "新增字典项"
      }
      open={isOpen}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
      width={640}
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" preserve={false}>
        {(creatingItem || item?.nodeType === "ITEM") ? (
          <Form.Item
            name="parentId"
            label="父级字典"
            rules={
              creatingItem || item?.nodeType === "ITEM" || parentForCreate
                ? [{ required: true, message: "请选择父级字典" }]
                : []
            }
          >
            <TreeSelect
              allowClear
              treeDefaultExpandAll
              placeholder={parentForCreate ? "请选择父级（可选类型或二级项）" : "请选择父级"}
              treeData={parentOptions}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          name="name"
          label="字典名称"
          rules={[
            { required: true, message: "请输入字典名称" },
            { max: 64, message: "字典名称不能超过 64 个字符" },
          ]}
        >
          <Input placeholder="请输入字典名称" maxLength={64} />
        </Form.Item>

        <Form.Item
          name="code"
          label="字典编码"
          rules={[
            { required: true, message: DICT_CODE_RULES.required },
            {
              pattern: DICT_CODE_PATTERN,
              message: DICT_CODE_RULES.pattern,
            },
            {
              max: DICT_CODE_RULES.max,
              message: `字典编码不能超过 ${DICT_CODE_RULES.max} 个字符`,
            },
          ]}
        >
          <Input placeholder="请输入字典编码，例如 LEAD_SOURCE" maxLength={DICT_CODE_RULES.max} />
        </Form.Item>

        <Form.Item name="sort" label="排序">
          <InputNumber min={0} max={999999} className="w-full" />
        </Form.Item>

        <Form.Item name="isEnabled" label="是否启用" valuePropName="checked">
          <Switch checkedChildren="启用" unCheckedChildren="停用" />
        </Form.Item>

        <Form.Item
          name="note"
          label="备注"
          rules={[{ max: 500, message: "备注不能超过 500 个字符" }]}
        >
          <Input.TextArea rows={4} placeholder="请输入备注" maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default DictModal
